import { CncPartsEditor } from "@/components/CncPartsEditor";
import { ScreenHelp } from "@/components/Help";
import type { ScreenId } from "@/components/Shell";
import { LoadingState } from "@/components/States";
import { Card, Field, Icon, Pill, Select, TextInput, Textarea } from "@/components/ui";
import { fetchQuoteFileUrl, uploadQuoteFile } from "@/lib/api";
import {
  type CncMasterData,
  asNewQuoteSelection,
  emptyCncSelection,
  selectionToResult,
} from "@/lib/cost/cncForm";
import { cncResultToLineItem } from "@/lib/cost/cncProjection";
import {
  type CncPartSelection,
  type LineItem,
  MARGIN_FLOOR,
  STATUS,
  type StatusKey,
  fmt,
  ownerOptions,
  quoteTotals,
} from "@/lib/data";
import {
  createQuote,
  updateQuote,
  useCncLaborRates,
  useCncMachineExpenses,
  useCncRawMaterials,
  useCncSettings,
  useCncSurfaceTreatments,
  useCustomers,
  useQuote,
} from "@/lib/hooks";
import {
  MAX_QUOTE_FILES,
  type QuoteAttachment,
  formatFileSize,
  validateQuoteFile,
} from "@/lib/quoteFiles";
import { cols, useIsMobile } from "@/lib/useViewport";
import { useEffect, useRef, useState } from "react";

const NEW_QUOTE_HELP = [
  "고객사 · 프로젝트명(필수)과 담당자 · 양산 시점(SOP)을 입력합니다 — 견적번호는 자동 생성됩니다.",
  "세부원가 영역에서 파트별 소재 · 가공 · 표면처리를 선택하면 제품원가가 자동 산출되고 헤더의 원가 · 마진 요약에 반영됩니다.",
  "영업이익률이 최소 마진 미만이면 가드레일 경고 배너가 표시됩니다.",
  "저장 시점의 단가가 스냅샷으로 보존되어 이후 마스터 단가 변경에 영향받지 않습니다.",
];

type DraftFile = { name: string; size: number; key?: string; file?: File };

const SummaryStat = ({
  label,
  en,
  value,
  unit,
  accent,
  danger,
  muted,
}: {
  label: string;
  en: string;
  value: string;
  unit: string;
  accent?: boolean;
  danger?: boolean;
  muted?: boolean;
}) => {
  const mobile = useIsMobile();
  return (
    <div>
      <div style={{ fontSize: 12, color: "var(--text-dim)", marginBottom: 5 }}>
        {label}{" "}
        <span style={{ fontSize: 10, color: "var(--text-fainter)", letterSpacing: "0.4px" }}>
          {en}
        </span>
      </div>
      <div
        style={{
          fontFamily: "var(--font-title)",
          fontWeight: 800,
          fontSize: mobile ? 18 : 23,
          letterSpacing: "-0.01em",
          color: danger
            ? "var(--text-negative)"
            : accent
              ? "var(--brand)"
              : muted
                ? "var(--text-near)"
                : "var(--text-base)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
        <span style={{ fontSize: 13, fontWeight: 700, marginLeft: 3, color: "var(--text-dim)" }}>
          {unit}
        </span>
      </div>
    </div>
  );
};

export const NewQuoteScreen = ({
  quoteId = null,
  copyFromId = null,
  onNavigate,
  onToast,
}: {
  // 값이 있으면 기존 견적(임시저장 등) 편집 모드 — 불러와서 프리필 후 PUT 으로 저장.
  quoteId?: string | null;
  // 값이 있으면 복사 모드 — 원본 견적을 프리필하되 새 견적(POST)으로 저장한다(견적 리스트 행 복사).
  copyFromId?: string | null;
  onNavigate: (id: ScreenId) => void;
  onToast: (msg: string) => void;
}) => {
  const mobile = useIsMobile();
  const customersRes = useCustomers();
  // 편집(quoteId) 또는 복사(copyFromId) 원본을 불러온다. 복사 모드는 quoteId 가 없어 저장 시 POST.
  const loadId = quoteId ?? copyFromId;
  const editRes = useQuote(loadId);
  const customers = customersRes.data;

  // 견적 유형은 CNC 단일 (단조/외주 확장 계획 폐기됨) — buildQuote 에서 "cnc" 고정 저장.

  const cncRaw = useCncRawMaterials();
  const cncSurface = useCncSurfaceTreatments();
  const cncLabor = useCncLaborRates();
  const cncExpense = useCncMachineExpenses();
  const cncSettings = useCncSettings();

  const [cncParts, setCncParts] = useState<CncPartSelection[]>(() => [
    emptyCncSelection(cncSettings.data),
  ]);
  const cncMaster: CncMasterData = {
    raw: cncRaw.data,
    surface: cncSurface.data,
    labor: cncLabor.data,
    expense: cncExpense.data,
    settings: cncSettings.data,
  };

  const [project, setProject] = useState("");
  const [custId, setCustId] = useState("");
  const [status, setStatus] = useState<StatusKey>("draft");
  // 양산 시점(SOP) — 연/월/일 단위 날짜. 기본값 없음(사용자 입력).
  const [sop, setSop] = useState("");
  const [owner, setOwner] = useState("");
  const [saving, setSaving] = useState(false);
  // 기존 첨부는 key 보유, 새 첨부는 File 보유 — 저장 시점에 새 것만 업로드한다.
  const [files, setFiles] = useState<DraftFile[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  // 비고 — 견적 전반에 대한 자유 입력 메모. 저장 시 스냅샷의 일부로 보존된다.
  const [remark, setRemark] = useState("");
  const [purpose, setPurpose] = useState("");

  // 편집/복사 프리필 — 원본 견적이 로드되면 헤더/파트 상태를 1회 채운다.
  // 복사 모드(quoteId 없음)는 새 견적이므로 상태를 '작성중'으로 두고 프로젝트명에 (복사)를 붙인다.
  const prefilled = useRef(false);
  useEffect(() => {
    const q = editRes.data;
    const copying = !quoteId;
    // 복사는 재질별 기본 회수율을 채워야 하므로 원재료 마스터 로딩을 기다린다(내용추가18).
    if (!loadId || !q || prefilled.current || (copying && cncRaw.loading)) return;
    prefilled.current = true;
    setProject(copying ? `${q.project} (복사)` : q.project);
    setCustId(q.customerId);
    setStatus(copying ? "draft" : q.status);
    setOwner(q.owner);
    if (q.sop) setSop(q.sop);
    if (q.purpose) setPurpose(q.purpose);
    if (q.remark) setRemark(q.remark);
    setFiles(q.attachments);
    if (q.cnc.length) {
      // 편집은 저장된 그대로(회수율 없는 기존 견적은 100% 유지), 복사본은 새 견적이므로
      // 회수율이 없으면 재질 기본 회수율을 채운다("새로 쓰는 견적부터 적용").
      setCncParts(
        q.cnc.map((p) =>
          copying ? asNewQuoteSelection(p.selection, { raw: cncRaw.data }) : p.selection
        )
      );
    }
  }, [loadId, quoteId, editRes.data, cncRaw.loading, cncRaw.data]);

  const cust = customers.find((c) => c.id === custId);

  // CNC 파트별 산출 결과를 합성 LineItem 으로 투영해 totals/요약을 구동.
  const cncItems: LineItem[] = cncParts.map((sel, i) => {
    const result = selectionToResult(sel, cncMaster);
    const matName = cncMaster.raw.find((r) => r.id === sel.materialId)?.name ?? `파트 ${i + 1}`;
    const procName = cncMaster.labor.find((l) => l.id === sel.rows[0]?.laborRateId)?.process ?? "";
    return cncResultToLineItem(result, { item: matName, spec: procName }, i + 1);
  });
  const effTotals = quoteTotals(cncItems);
  const effLowMargin = effTotals.profitRate < MARGIN_FLOOR;

  const addFiles = (incoming: FileList | File[]) => {
    const next = [...files];
    for (const f of Array.from(incoming)) {
      const err = validateQuoteFile(f, next.length);
      if (err) {
        onToast(err);
        break;
      }
      next.push({ name: f.name, size: f.size, file: f });
    }
    setFiles(next);
  };

  // 인증 헤더가 필요해 <a href> 직접 사용 불가 — blob URL 로 받아 다운로드한다.
  const openFile = async (f: DraftFile) => {
    try {
      const url = f.file ? URL.createObjectURL(f.file) : await fetchQuoteFileUrl(f.key ?? "");
      const a = document.createElement("a");
      a.href = url;
      a.download = f.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      onToast(e instanceof Error ? e.message : "파일을 불러오지 못했습니다");
    }
  };

  // 새로 고른 파일(File 보유)만 업로드하고, 기존 첨부는 key 를 그대로 보존한다.
  const uploadPendingFiles = async (): Promise<QuoteAttachment[]> => {
    const attachments: QuoteAttachment[] = [];
    for (const f of files) {
      if (f.key) attachments.push({ key: f.key, name: f.name, size: f.size });
      else if (f.file) attachments.push(await uploadQuoteFile(f.file));
    }
    return attachments;
  };

  const buildQuote = (saveStatus: StatusKey, attachments: QuoteAttachment[]) => {
    const cnc = cncParts.map((selection) => ({
      selection,
      result: selectionToResult(selection, cncMaster),
    }));
    return {
      project,
      customerId: custId,
      status: saveStatus,
      owner,
      approver: "",
      sop,
      purpose,
      attachments,
      // 비고는 비어 있어도 항상 전송 — 서버가 "보낸 필드만" 덮어쓰므로(미전송=보존)
      // 생략하면 비고를 지울 수 없다.
      remark: remark.trim(),
      quoteType: "cnc" as const,
      cnc,
      items: cncItems,
    };
  };

  const save = async (saveStatus: StatusKey, okMsg: string) => {
    if (!custId || !project.trim()) {
      onToast("고객사와 프로젝트명을 입력하세요");
      return;
    }
    setSaving(true);
    try {
      const attachments = await uploadPendingFiles();
      if (quoteId) await updateQuote(quoteId, buildQuote(saveStatus, attachments));
      else await createQuote(buildQuote(saveStatus, attachments));
      onToast(okMsg);
      onNavigate("list");
    } catch (e) {
      onToast(e instanceof Error ? e.message : "저장에 실패했습니다");
    } finally {
      setSaving(false);
    }
  };

  if (
    customersRes.loading ||
    editRes.loading ||
    cncRaw.loading ||
    cncSurface.loading ||
    cncLabor.loading ||
    cncExpense.loading ||
    cncSettings.loading
  ) {
    return (
      <div style={{ padding: "24px 28px 56px" }}>
        <LoadingState label="고객사 정보 불러오는 중…" />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: mobile ? "16px 14px 120px" : "24px 28px 120px",
        display: "flex",
        flexDirection: "column",
        gap: 18,
      }}
    >
      <ScreenHelp items={NEW_QUOTE_HELP} />
      {/* Header form */}
      <Card pad={24}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 18 }}>
            견적서 정보
          </div>
          <span style={{ fontSize: 11.5, color: "var(--text-faint)", letterSpacing: "0.5px" }}>
            HEADER INFO
          </span>
          <div style={{ flex: 1 }} />
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              background: "var(--bg-surface-2)",
              borderRadius: 6,
              padding: "8px 14px",
              fontFamily: "ui-monospace, monospace",
              fontSize: 13.5,
              color: "var(--text-base)",
            }}
          >
            <span style={{ color: "var(--brand)", fontWeight: 700 }}>
              {quoteId ?? "QT-YYYYMMDD-XXXXXX"}
            </span>
            <span style={{ fontSize: 10.5, color: "var(--text-faint)", letterSpacing: "0.5px" }}>
              {quoteId ? "기존 견적 수정" : "저장 시 자동 생성"}
            </span>
          </span>
        </div>
        <div
          style={{ display: "grid", gridTemplateColumns: cols(mobile, "repeat(4, 1fr)"), gap: 16 }}
        >
          <Field label="고객사" en="Customer" required style={{ gridColumn: "span 1" }}>
            <Select
              value={custId}
              onChange={setCustId}
              placeholder="고객사 선택"
              options={customers.map((c) => ({ value: c.id, label: c.name }))}
            />
          </Field>
          <Field
            label="프로젝트명"
            en="Project"
            required
            style={{ gridColumn: mobile ? "auto" : "span 2" }}
          >
            <TextInput
              value={project}
              onChange={setProject}
              placeholder="예: 로봇 관절 구동부 가공 (2026 1차)"
            />
          </Field>
          <Field label="진행 상태" en="Status">
            <Select
              value={status}
              onChange={(v) => setStatus(v as StatusKey)}
              options={(Object.keys(STATUS) as StatusKey[]).map((k) => ({
                value: k,
                label: STATUS[k].label,
              }))}
            />
          </Field>
          <Field
            label="담당자"
            en="Owner"
            hint={
              !cust ? "고객사 선택 후" : !cust.contacts.length ? "등록된 담당자 없음" : undefined
            }
          >
            <Select
              value={owner}
              onChange={setOwner}
              placeholder={cust ? "담당자 선택" : "고객사 먼저 선택"}
              options={ownerOptions(cust, owner)}
            />
          </Field>
          <Field label="양산 시점" en="SOP" hint="제품 양산 시점 (연/월/일)">
            <TextInput type="date" value={sop} onChange={setSop} mono />
          </Field>
          <Field label="제출 용도" en="Purpose" required>
            <Select
              value={purpose}
              onChange={setPurpose}
              placeholder="선택하세요"
              options={[
                { value: "RFQ용", label: "RFQ용" },
                { value: "업체선정용", label: "업체선정용" },
                { value: "재견적", label: "재견적" },
                { value: "샘플견적", label: "샘플견적" },
              ]}
            />
          </Field>
        </div>
      </Card>

      {/* Guardrail banner */}
      {effLowMargin && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 20px",
            background: "rgba(243,114,127,0.1)",
            border: "1px solid rgba(243,114,127,0.35)",
            borderRadius: 12,
          }}
        >
          <Icon name="alert" size={20} color="var(--text-negative)" />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: "var(--text-negative)", fontSize: 14 }}>
              마진 가드레일 경고 — 영업이익률 {effTotals.profitRate.toFixed(1)}%
            </div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
              최소 기준 {MARGIN_FLOOR}% 미만입니다. 단가 또는 마진율을 확인하세요.
            </div>
          </div>
        </div>
      )}

      {/* Detail section: CNC parts editor */}
      <Card pad={20} style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
          <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 18 }}>
            세부원가
          </div>
          <span style={{ fontSize: 11.5, color: "var(--text-faint)", letterSpacing: "0.5px" }}>
            PARTS
          </span>
        </div>
        <CncPartsEditor value={cncParts} onChange={setCncParts} master={cncMaster} />
      </Card>

      {/* Attachments + totals */}
      <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1fr 1.3fr"), gap: 16 }}>
        <Card pad={24}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
            <Icon name="paperclip" size={17} color="var(--text-muted)" />
            <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 16 }}>
              도면 · 첨부파일
            </div>
            <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
              최대 5개 · 파일당 10MB
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            {files.map((f, i) => (
              <div
                key={`${f.key ?? f.name}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  background: "var(--bg-surface-2)",
                  borderRadius: 6,
                  padding: "10px 12px",
                }}
              >
                <Icon name="file" size={16} color="var(--text-announcement)" />
                <button
                  type="button"
                  onClick={() => openFile(f)}
                  title="클릭하여 다운로드"
                  style={{
                    flex: 1,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    background: "transparent",
                    border: 0,
                    color: "inherit",
                    cursor: "pointer",
                    textAlign: "left",
                    padding: 0,
                  }}
                >
                  {f.name}
                </button>
                <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
                  {formatFileSize(f.size)}
                </span>
                <button
                  type="button"
                  onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}
                  style={{
                    background: "transparent",
                    border: 0,
                    color: "var(--text-fainter)",
                    cursor: "pointer",
                    display: "flex",
                  }}
                >
                  <Icon name="x" size={15} />
                </button>
              </div>
            ))}
          </div>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept=".pdf,.dwg,.dxf,.dwf,.step,.stp,.igs,.iges,.png,.jpg,.jpeg,.gif,.webp"
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <div
            onClick={() => files.length < MAX_QUOTE_FILES && fileInput.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
            }}
            style={{
              border: "1.5px dashed #3a3a3a",
              borderRadius: 12,
              padding: "20px",
              textAlign: "center",
              cursor: files.length < MAX_QUOTE_FILES ? "pointer" : "default",
              color: "var(--text-dim)",
              fontSize: 13,
              opacity: files.length < MAX_QUOTE_FILES ? 1 : 0.5,
            }}
          >
            <Icon name="download" size={20} style={{ margin: "0 auto 6px" }} />
            <div>클릭 또는 드래그하여 도면(PDF/CAD)·사진 업로드</div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Field label="비고" en="Remark" hint="견적 전반에 대한 메모 — 저장 시 함께 보존됩니다.">
              <Textarea
                value={remark}
                onChange={setRemark}
                placeholder="특이사항·납기·요청사항 등을 입력하세요"
                rows={3}
                maxLength={1000}
              />
            </Field>
          </div>
        </Card>

        <Card pad={24} style={{ background: "var(--bg-card-hover)" }}>
          <div
            style={{
              fontFamily: "var(--font-title)",
              fontWeight: 800,
              fontSize: 16,
              marginBottom: 18,
            }}
          >
            원가 · 마진 요약
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: cols(mobile, "1fr 1fr"),
              gap: 14,
              marginBottom: 18,
            }}
          >
            <SummaryStat
              label="내부 총원가"
              en="Internal Cost"
              value={fmt(effTotals.internal)}
              unit="원"
              muted
            />
            <SummaryStat
              label="최종 제출 금액"
              en="Submit Total"
              value={fmt(effTotals.submit)}
              unit="원"
              accent
            />
            <SummaryStat
              label="예상 영업이익"
              en="Profit"
              value={fmt(effTotals.profit)}
              unit="원"
            />
            <SummaryStat
              label="영업이익률"
              en="Margin"
              value={effTotals.profitRate.toFixed(1)}
              unit="%"
              danger={effLowMargin}
            />
          </div>
          <div
            style={{
              marginBottom: 6,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11.5,
              color: "var(--text-faint)",
            }}
          >
            <span>이익률 가드레일 (최소 {MARGIN_FLOOR}%)</span>
            <span
              style={{
                color: effLowMargin ? "var(--num-negative)" : "var(--num-positive)",
                fontWeight: 700,
              }}
            >
              {effTotals.profitRate.toFixed(1)}%
            </span>
          </div>
          <div
            style={{
              height: 8,
              background: "var(--bg-surface-3)",
              borderRadius: 4,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                width: `${Math.min(100, effTotals.profitRate * 3)}%`,
                height: "100%",
                background: effLowMargin ? "var(--num-negative)" : "var(--num-positive)",
                transition: "width .3s, background .3s",
              }}
            />
            <div
              style={{
                position: "absolute",
                left: `${MARGIN_FLOOR * 3}%`,
                top: -2,
                bottom: -2,
                width: 2,
                background: "var(--text-base)",
                opacity: 0.5,
              }}
            />
          </div>
        </Card>
      </div>

      {/* Sticky action bar */}
      <div
        style={{
          position: "fixed",
          left: mobile ? 0 : 264,
          right: 0,
          bottom: 0,
          zIndex: 30,
          background: "var(--bg-topbar)",
          backdropFilter: "blur(16px) saturate(140%)",
          WebkitBackdropFilter: "blur(16px) saturate(140%)",
          borderTop: "1px solid var(--bg-card)",
          padding: mobile ? "12px 14px" : "14px 28px",
          display: "flex",
          alignItems: "center",
          flexWrap: mobile ? "wrap" : "nowrap",
          gap: mobile ? 10 : 16,
        }}
      >
        {!mobile && <div style={{ fontSize: 13, color: "var(--text-dim)" }}>제출 금액</div>}
        <div
          style={{
            fontFamily: "var(--font-title)",
            fontWeight: 900,
            fontSize: mobile ? 18 : 24,
            color: "var(--text-base)",
          }}
        >
          {fmt(effTotals.submit)} <span style={{ fontSize: 15 }}>원</span>
        </div>
        {effLowMargin && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              color: "var(--text-negative)",
              fontSize: 12.5,
              fontWeight: 700,
            }}
          >
            <Icon name="alert" size={14} />
            마진 미달
          </span>
        )}
        <div style={{ flex: 1 }} />
        <Pill
          variant="dark"
          size="md"
          icon={<Icon name="save" size={16} />}
          disabled={saving}
          onClick={() => void save("draft", "임시저장되었습니다")}
        >
          임시저장
        </Pill>
        <Pill
          variant="primary"
          size="md"
          icon={<Icon name="check" size={16} stroke={2.6} />}
          disabled={saving}
          onClick={() =>
            void save(status === "draft" ? "submitted" : status, "견적이 제출되었습니다")
          }
        >
          제출하기
        </Pill>
      </div>
    </div>
  );
};
